import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";

import { t } from "@/i18n-js/instance";
import { ErrorTemplate } from "@/react/components/shared/ErrorPages/ErrorTemplate";
import { useFetchCheckoutData } from "@/react/hooks/paywalls/useFetchCheckoutData";

import { Checkout } from "../../Checkout";
import { CheckoutPage } from "../../Checkout/CheckoutPage";
import type { PaywallCheckout } from "../../Checkout/types/CheckoutProps";

interface ViewAsMainOfferPageProps {
  paywallId: number;
}

// Renders the checkout main offer page in View As (masquerade) mode.
//
// In View As mode all non-GET requests are blocked, so the normal
// CheckoutSessionProvider (which POSTs to create a session on mount) cannot be
// used. Instead, we hydrate the page from the existing GET endpoint
// (/internal_api/paywalls/checkout?paywall_id=X) and render the offer page
// directly — no session required. If the admin clicks the CTA the POST will
// naturally be blocked by View As, so no additional disabling is needed.
export const ViewAsMainOfferPage = ({
  paywallId,
}: ViewAsMainOfferPageProps) => {
  const { currentCommunityMember, currentUser } = usePunditUserContext();
  const { showQuery } = useFetchCheckoutData({
    paywall_id: paywallId,
  });

  if (showQuery.isPending) {
    return <CheckoutPage.Skeleton />;
  }

  if (showQuery.isError || !showQuery.data) {
    return (
      <ErrorTemplate
        errorCode={500}
        title={t("paywall_checkout.error_boundary.title")}
      />
    );
  }

  const data = showQuery.data;

  if (!data.paywall) {
    return (
      <ErrorTemplate
        errorCode={404}
        title={t("paywall_checkout.error_boundary.title")}
      />
    );
  }

  const {
    paywall,
    community,
    community_payment_processor,
    community_member_payment_processor,
    community_member,
    policies,
    settings,
    auth_provider,
  } = data;

  return (
    <Checkout
      paywall={paywall as unknown as PaywallCheckout}
      currentCommunity={community}
      currentCommunityProcessorPaymentMethodConfigurationId={
        community_payment_processor.payment_method_configuration_id
      }
      isLinkPreferenceEnabled={
        community_payment_processor.is_link_capability_active
      }
      isMemberWithSavedPaymentMethods={
        community_member_payment_processor.is_member_with_saved_payment_methods
      }
      currentCommunityMemberPaymentProcessorSessionClientSecret={undefined}
      currentUser={currentUser}
      currentCommunityMember={currentCommunityMember}
      memberBillingInfo={community_member?.billing_info}
      hasAlreadyPurchased={policies.has_already_purchased}
      loginUrl={settings.login_url}
      resetPasswordUrl={settings.reset_password_url}
      communityRootUrl={settings.community_url}
      checkoutConfirmationUrl={settings.checkout_confirmation_url}
      processorId={community_payment_processor.processor_id}
      processorKey={community_payment_processor.public_api_key}
      areCouponsEnabled={policies.are_coupons_enabled}
      isTaxCollectionEnabled={policies.is_tax_collection_enabled}
      isMemberAddressComplete={policies.is_member_address_complete}
      isMemberAddressRequired={policies.is_member_address_required}
      isBrazilianAccountAndProcessorStripeBr={
        community_payment_processor.is_brazilian_account_and_processor_stripe_brazil
      }
      shouldShowTaxIdForm={policies.should_show_tax_id_form}
      isTaxIdMandatory={community_payment_processor.is_tax_id_mandatory}
      authProviderProperties={auth_provider}
      shouldUseWrapper={false}
    />
  );
};
